/**
 * POS Reconciliation Dashboard
 * 
 * Manager-facing dashboard for monitoring and recovering offline POS operations
 * across all branches and terminals.
 */

import { useState } from 'react';
import {
  Cloud,
  CloudOff,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Store,
  Monitor,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Filter,
  Trash2,
  MessageSquare,
  Eye,
  ShoppingCart,
  CreditCard,
  Receipt,
  TrendingUp,
  AlertTriangle,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { usePOSReconciliation } from '@/hooks/usePOSReconciliation';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { getPOSActionDisplayName } from '@/lib/offline/posPolicies';
import { useNavigate } from 'react-router-dom';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

// Format time ago
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const mins = Math.floor((Date.now() - timestamp) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// Status badge config
const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Pending' },
  syncing: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Syncing', animate: true },
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Synced' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Failed' },
  conflict: { icon: AlertCircle, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Conflict' }
};

/**
 * Summary Stats Grid
 */
function SummaryStatsGrid({ counts, isOnline }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      <Card>
        <CardContent className="p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            {isOnline ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-amber-500" />
            )}
          </div>
          <p className="text-2xl font-bold text-blue-600">{counts.pending}</p>
          <p className="text-xs text-muted-foreground">Pending Sync</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{counts.failed}</p>
          <p className="text-xs text-muted-foreground">Failed</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{counts.conflict}</p>
          <p className="text-xs text-muted-foreground">Conflicts</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4 text-center">
          <ShoppingCart className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
          <p className="text-2xl font-bold">{counts.orders}</p>
          <p className="text-xs text-muted-foreground">Orders</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4 text-center">
          <CreditCard className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
          <p className="text-2xl font-bold">{counts.payments}</p>
          <p className="text-xs text-muted-foreground">Payments</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4 text-center">
          <DollarSign className="h-4 w-4 mx-auto mb-1 text-green-500" />
          <p className="text-2xl font-bold text-green-600">{formatCurrency(counts.totalAmount)}</p>
          <p className="text-xs text-muted-foreground">Pending Amount</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Terminal Health Card
 */
function TerminalHealthCard({ terminal, onRetry, onViewDetails, onViewIncidents }) {
  const hasIssues = terminal.failed > 0 || terminal.conflict > 0;
  const totalPending = terminal.pending + terminal.syncing;
  
  return (
    <Card className={cn(
      "transition-all",
      hasIssues && "border-red-500/50"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Monitor className={cn(
              "h-5 w-5",
              hasIssues ? "text-red-500" : totalPending > 0 ? "text-amber-500" : "text-green-500"
            )} />
            <div>
              <h3 className="font-semibold">{terminal.terminalId}</h3>
              <p className="text-xs text-muted-foreground">Branch: {terminal.branchId}</p>
            </div>
          </div>
          
          <div className="flex gap-1">
            {hasIssues && onViewIncidents && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewIncidents(terminal.terminalId)}
                className="text-xs"
                title="View incidents for this terminal"
              >
                <AlertTriangle className="h-3 w-3" />
              </Button>
            )}
            {hasIssues && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetry(terminal.terminalId)}
                className="text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Retry All
              </Button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          <div>
            <p className="text-lg font-bold text-blue-600">{totalPending}</p>
            <p className="text-muted-foreground">Pending</p>
          </div>
          <div>
            <p className="text-lg font-bold text-red-600">{terminal.failed}</p>
            <p className="text-muted-foreground">Failed</p>
          </div>
          <div>
            <p className="text-lg font-bold text-purple-600">{terminal.conflict}</p>
            <p className="text-muted-foreground">Conflicts</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-600">{terminal.completed}</p>
            <p className="text-muted-foreground">Synced</p>
          </div>
        </div>
        
        {terminal.totalAmount > 0 && (
          <div className="mt-3 pt-3 border-t flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Pending Amount</span>
            <span className="font-semibold text-green-600">{formatCurrency(terminal.totalAmount)}</span>
          </div>
        )}
        
        {terminal.lastActivity && (
          <p className="text-xs text-muted-foreground mt-2">
            Last activity: {formatTimeAgo(terminal.lastActivity)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Branch Overview Card
 */
function BranchOverviewCard({ branch, onRetry, onViewIncidents }) {
  const hasIssues = branch.failed > 0 || branch.conflict > 0;
  const totalPending = branch.pending + branch.syncing;
  
  return (
    <Card className={cn(
      "transition-all",
      hasIssues && "border-red-500/50"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Store className={cn(
              "h-5 w-5",
              hasIssues ? "text-red-500" : totalPending > 0 ? "text-amber-500" : "text-green-500"
            )} />
            <div>
              <h3 className="font-semibold">{branch.branchName}</h3>
              <p className="text-xs text-muted-foreground">{branch.terminals} terminals</p>
            </div>
          </div>
          
          <div className="flex gap-1">
            {hasIssues && onViewIncidents && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewIncidents(branch.branchId)}
                className="text-xs"
                title="View incidents for this branch"
              >
                <AlertTriangle className="h-3 w-3" />
              </Button>
            )}
            {hasIssues && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetry(branch.branchId)}
                className="text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Retry Branch
              </Button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
          <div>
            <p className="text-lg font-bold text-blue-600">{totalPending}</p>
            <p className="text-muted-foreground">Pending</p>
          </div>
          <div>
            <p className="text-lg font-bold text-red-600">{branch.failed + branch.conflict}</p>
            <p className="text-muted-foreground">Issues</p>
          </div>
          <div>
            <p className="text-lg font-bold text-green-600">{formatCurrency(branch.totalAmount)}</p>
            <p className="text-muted-foreground">Amount</p>
          </div>
        </div>
        
        {branch.terminalList && branch.terminalList.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {branch.terminalList.map(tid => (
              <Badge key={tid} variant="outline" className="text-xs">
                {tid}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Queue Item Row
 */
function QueueItemRow({ item, onRetry, onDiscard, onViewDetails, onAddNote }) {
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg mb-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={cn("p-2 rounded-lg", statusConfig.bg)}>
          <StatusIcon className={cn("h-4 w-4", statusConfig.color, statusConfig.animate && "animate-spin")} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{item.actionDisplayName}</span>
            <Badge variant="outline" className="text-xs">{item.terminalId}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatTimeAgo(item.createdAt)}
            {item.payload?.total && ` • ${formatCurrency(item.payload.total)}`}
          </p>
          {item.lastError && (
            <p className="text-xs text-red-500 truncate mt-1">{item.lastError}</p>
          )}
        </div>
      </div>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">Actions</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {(item.status === 'failed' || item.status === 'conflict') && (
            <DropdownMenuItem onClick={() => onRetry(item.id)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Retry
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onViewDetails(item)}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddNote(item)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Add Note
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDiscard(item.id)} className="text-red-600">
            <Trash2 className="h-4 w-4 mr-2" />
            Discard
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Main Dashboard Component
 */
export default function POSReconciliationPage() {
  const navigate = useNavigate();
  const { isOnline, isSyncing } = useOfflineSync();
  const {
    allItems,
    filteredItems,
    terminalStats,
    branchStats,
    summaryCounts,
    itemsByStatus,
    filterOptions,
    loading,
    lastRefresh,
    filters,
    setFilters,
    refreshData,
    retryItem,
    discardItem,
    retryTerminal,
    retryBranch,
    retryAll,
    syncNow,
    markReviewed,
    addNote
  } = usePOSReconciliation();

  const [activeTab, setActiveTab] = useState('overview');
  const [noteDialog, setNoteDialog] = useState({ open: false, item: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, item: null });
  const [noteText, setNoteText] = useState('');

  // Cross-link to Incident Audit Trail
  const handleViewTerminalIncidents = (terminalId) => {
    navigate(`${bo('incident-audit')}?terminalId=${terminalId}&unresolvedOnly=true`);
  };

  const handleViewLocationIncidents = (locationId) => {
    navigate(`${bo('incident-audit')}?locationId=${locationId}&unresolvedOnly=true`);
  };

  const handleViewPaymentIncidents = () => {
    navigate(`${bo('incident-audit')}?domain=payments&unresolvedOnly=true`);
  };

  // Handle actions
  const handleRetryItem = async (itemId) => {
    const result = await retryItem(itemId);
    if (result.success) {
      toast.success('Item queued for retry');
    } else {
      toast.error(`Retry failed: ${result.error}`);
    }
  };

  const handleDiscardItem = async (itemId) => {
    const result = await discardItem(itemId);
    if (result.success) {
      toast.success('Item discarded');
    } else {
      toast.error(`Discard failed: ${result.error}`);
    }
  };

  const handleRetryTerminal = async (terminalId) => {
    const result = await retryTerminal(terminalId);
    if (result.success) {
      toast.success(`${result.count} items queued for retry`);
    } else {
      toast.error(`Retry failed: ${result.error}`);
    }
  };

  const handleRetryBranch = async (branchId) => {
    const result = await retryBranch(branchId);
    if (result.success) {
      toast.success(`${result.count} items queued for retry`);
    } else {
      toast.error(`Retry failed: ${result.error}`);
    }
  };

  const handleSyncNow = async () => {
    const result = await syncNow();
    if (result.success) {
      toast.success('Sync started');
    } else {
      toast.error(`Sync failed: ${result.error}`);
    }
  };

  const handleRetryAll = async () => {
    const result = await retryAll();
    if (result.success) {
      toast.success(`${result.count} items queued for retry`);
    } else {
      toast.error(`Retry failed: ${result.error}`);
    }
  };

  const handleOpenNoteDialog = (item) => {
    setNoteText(item.managerNote || '');
    setNoteDialog({ open: true, item });
  };

  const handleSaveNote = async () => {
    if (!noteDialog.item) return;
    const result = await addNote(noteDialog.item.id, noteText, 'Manager');
    if (result.success) {
      toast.success('Note saved');
      setNoteDialog({ open: false, item: null });
    } else {
      toast.error(`Failed to save note: ${result.error}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pos-reconciliation-loading">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="pos-reconciliation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            POS Reconciliation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and recover offline POS operations across all terminals
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn(
                  "flex items-center gap-1 px-3 py-1.5 rounded-full text-sm",
                  isOnline ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                           : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                )}>
                  {isOnline ? <Cloud className="h-4 w-4" /> : <CloudOff className="h-4 w-4" />}
                  {isOnline ? 'Online' : 'Offline'}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {isOnline ? 'Connected to server' : 'Working offline - changes will sync when online'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button variant="outline" size="sm" onClick={refreshData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          
          <Button 
            size="sm"
            onClick={handleSyncNow}
            disabled={!isOnline || isSyncing || summaryCounts.pending === 0}
          >
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Cloud className="h-4 w-4 mr-1" />
            )}
            Sync Now
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <SummaryStatsGrid counts={summaryCounts} isOnline={isOnline} />

      {/* Alert Banner */}
      {summaryCounts.needsAttention > 0 && (
        <Card className="border-red-500/50 bg-red-50 dark:bg-red-900/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="font-medium text-red-700 dark:text-red-400">
                {summaryCounts.needsAttention} items need attention
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRetryAll}
              disabled={!isOnline}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Retry All Failed
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select 
              value={filters.branch || 'all'} 
              onValueChange={(v) => setFilters(f => ({ ...f, branch: v === 'all' ? null : v }))}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {filterOptions.branches.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={filters.terminal || 'all'} 
              onValueChange={(v) => setFilters(f => ({ ...f, terminal: v === 'all' ? null : v }))}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Terminal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Terminals</SelectItem>
                {filterOptions.terminals.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={filters.status || 'all'} 
              onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? null : v }))}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="conflict">Conflict</SelectItem>
              </SelectContent>
            </Select>
            
            {(filters.branch || filters.terminal || filters.status) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setFilters({ branch: null, terminal: null, status: null })}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="terminals">
            Terminals
            {terminalStats.length > 0 && (
              <Badge className="ml-1 h-5 px-1.5" variant="secondary">{terminalStats.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="branches">Branches</TabsTrigger>
          <TabsTrigger value="queue">
            Queue
            {summaryCounts.needsAttention > 0 && (
              <Badge className="ml-1 h-5 px-1.5 bg-red-500">{summaryCounts.needsAttention}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Activity */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Activity</CardTitle>
                <CardDescription>Latest POS sync events</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {allItems.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Receipt className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No offline POS activity</p>
                    </div>
                  ) : (
                    allItems.slice(0, 10).map(item => (
                      <QueueItemRow
                        key={item.id}
                        item={item}
                        onRetry={handleRetryItem}
                        onDiscard={handleDiscardItem}
                        onViewDetails={(i) => setDetailsDialog({ open: true, item: i })}
                        onAddNote={handleOpenNoteDialog}
                      />
                    ))
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Terminal Status Overview */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Terminal Health</CardTitle>
                <CardDescription>Status of active terminals</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {terminalStats.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Monitor className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No terminals with pending sync</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {terminalStats.map(terminal => (
                        <TerminalHealthCard
                          key={terminal.terminalId}
                          terminal={terminal}
                          onRetry={handleRetryTerminal}
                          onViewDetails={() => {}}
                          onViewIncidents={handleViewTerminalIncidents}
                        />
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Terminals Tab */}
        <TabsContent value="terminals" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {terminalStats.length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No terminals with pending sync items</p>
              </div>
            ) : (
              terminalStats.map(terminal => (
                <TerminalHealthCard
                  key={terminal.terminalId}
                  terminal={terminal}
                  onRetry={handleRetryTerminal}
                  onViewDetails={() => {}}
                  onViewIncidents={handleViewTerminalIncidents}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Branches Tab */}
        <TabsContent value="branches" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branchStats.length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <Store className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No branches with pending sync items</p>
              </div>
            ) : (
              branchStats.map(branch => (
                <BranchOverviewCard
                  key={branch.branchId}
                  branch={branch}
                  onRetry={handleRetryBranch}
                  onViewIncidents={handleViewLocationIncidents}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Queue Tab */}
        <TabsContent value="queue" className="mt-4">
          <Tabs defaultValue="pending">
            <TabsList>
              <TabsTrigger value="pending">
                Pending
                {itemsByStatus.pending.length > 0 && (
                  <Badge className="ml-1" variant="secondary">{itemsByStatus.pending.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="failed">
                Failed
                {itemsByStatus.failed.length > 0 && (
                  <Badge className="ml-1 bg-red-500">{itemsByStatus.failed.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="conflict">
                Conflicts
                {itemsByStatus.conflict.length > 0 && (
                  <Badge className="ml-1 bg-purple-500">{itemsByStatus.conflict.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent value="pending">
                <ScrollArea className="h-[400px]">
                  {itemsByStatus.pending.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No pending items</p>
                    </div>
                  ) : (
                    itemsByStatus.pending.map(item => (
                      <QueueItemRow
                        key={item.id}
                        item={item}
                        onRetry={handleRetryItem}
                        onDiscard={handleDiscardItem}
                        onViewDetails={(i) => setDetailsDialog({ open: true, item: i })}
                        onAddNote={handleOpenNoteDialog}
                      />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="failed">
                {itemsByStatus.failed.length > 0 && (
                  <div className="mb-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleRetryAll}
                      disabled={!isOnline}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retry All Failed ({itemsByStatus.failed.length})
                    </Button>
                  </div>
                )}
                <ScrollArea className="h-[400px]">
                  {itemsByStatus.failed.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No failed items</p>
                    </div>
                  ) : (
                    itemsByStatus.failed.map(item => (
                      <QueueItemRow
                        key={item.id}
                        item={item}
                        onRetry={handleRetryItem}
                        onDiscard={handleDiscardItem}
                        onViewDetails={(i) => setDetailsDialog({ open: true, item: i })}
                        onAddNote={handleOpenNoteDialog}
                      />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="conflict">
                <ScrollArea className="h-[400px]">
                  {itemsByStatus.conflict.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No conflicts</p>
                    </div>
                  ) : (
                    itemsByStatus.conflict.map(item => (
                      <QueueItemRow
                        key={item.id}
                        item={item}
                        onRetry={handleRetryItem}
                        onDiscard={handleDiscardItem}
                        onViewDetails={(i) => setDetailsDialog({ open: true, item: i })}
                        onAddNote={handleOpenNoteDialog}
                      />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="completed">
                <ScrollArea className="h-[400px]">
                  {itemsByStatus.completed.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No completed items yet</p>
                    </div>
                  ) : (
                    itemsByStatus.completed.map(item => (
                      <QueueItemRow
                        key={item.id}
                        item={item}
                        onRetry={handleRetryItem}
                        onDiscard={handleDiscardItem}
                        onViewDetails={(i) => setDetailsDialog({ open: true, item: i })}
                        onAddNote={handleOpenNoteDialog}
                      />
                    ))
                  )}
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(open) => !open && setNoteDialog({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manager Note</DialogTitle>
            <DialogDescription>
              Add a note to this sync item for tracking.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your note..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog({ open: false, item: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => !open && setDetailsDialog({ open: false, item: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {detailsDialog.item && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Action Type</Label>
                  <p className="font-medium">{detailsDialog.item.actionDisplayName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p className="font-medium">{detailsDialog.item.status}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Terminal</Label>
                  <p className="font-medium">{detailsDialog.item.terminalId}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Branch</Label>
                  <p className="font-medium">{detailsDialog.item.branchId}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p className="font-medium">{new Date(detailsDialog.item.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Retry Count</Label>
                  <p className="font-medium">{detailsDialog.item.retryCount || 0}</p>
                </div>
              </div>
              
              {detailsDialog.item.lastError && (
                <div>
                  <Label className="text-muted-foreground">Last Error</Label>
                  <p className="mt-1 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                    {detailsDialog.item.lastError}
                  </p>
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Payload</Label>
                <pre className="mt-1 p-2 rounded bg-muted text-xs overflow-auto max-h-40">
                  {JSON.stringify(detailsDialog.item.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialog({ open: false, item: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
